import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  Grid, 
  Heading3,
  BodyText,
  Flex,
  Badge
} from './ui';
import CinematicHero from './ui/CinematicHero';
import { DesignSystem, getColor as _getColor, getSpacing, getShadow } from '../styles/designSystem';
import { ItineraryAPI, ItineraryDetails, CarPricingTable as CarPricingTableType, CarPricingTier, CarPricingEntry } from '../api/itinerary';
import { ImageAPI } from '../api/images';
import BookingForm from './BookingForm';
import EnquiryDialog from './EnquiryDialog';
import AppHeader from './Header';
import { useHeaderItineraries } from '../hooks/useHeaderItineraries';
import { API_BASE_URL } from '../api/tourism';
import { 
  ChevronDown, 
  ChevronUp, 
  
  MapPin, 
  Moon,
  Sparkles, 
  ClipboardList, 
  Luggage, 
  HeartPulse, 
  Tag,
  Search,
  Image as ImageIcon,
  ArrowUp,
  Check,
  X,
  Circle,
  Maximize2,
  Minimize2,
  Calendar,
  Mountain,
  Route,
  IndianRupee,
  Activity,
  Clock,
  ChevronRight,
  Users,
  Car,
  Hotel,
  BedDouble,
  Phone
} from 'lucide-react';

// Luminous Gold palette — local override for detail page accent colors
// #CAB96E is the darkest accent shade; lighter tones tint toward white
const GOLD: Record<string, string> = {
  'primary.50': '#F8F8F6', 'primary.100': '#F4F3F0', 'primary.200': '#EEECE6',
  'primary.300': '#E6E4DA', 'primary.400': '#DEDAC9', 'primary.500': '#D4CEB3',
  'primary.600': '#CBC299', 'primary.700': '#CAB96E',
};
const getColor = (token: string) => GOLD[token] || _getColor(token);

// ─── Props ────────────────────────────────────────────────────────────────────

interface DetailedItineraryPageProps {
  state: string;
  itineraryId: string;
  onBack: () => void;
  onNavigateTo?: (page: string, data?: any) => void;
}

// ─── Animations ───────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ─── Scroll Reveal Hook ───────────────────────────────────────────────────────

const useScrollReveal = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
};

// ─── Scroll Progress Hook ─────────────────────────────────────────────────────

const useScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
      setShowBackToTop(scrollTop > 500);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return { progress, showBackToTop };
};

// ─── Scroll Spy Hook ──────────────────────────────────────────────────────────

const useScrollSpy = (dayCount: number) => {
  const [activeDay, setActiveDay] = useState(1);

  useEffect(() => {
    if (dayCount === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const dayNum = parseInt(entry.target.id.replace('day-', ''), 10);
            if (!isNaN(dayNum)) setActiveDay(dayNum);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-120px 0px -40% 0px' }
    );

    for (let i = 1; i <= dayCount; i++) {
      const el = document.getElementById(`day-${i}`);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [dayCount]);

  return activeDay;
};

// ─── Reveal Wrapper ───────────────────────────────────────────────────────────

const RevealSection = styled.div<{ isVisible: boolean; delay?: number }>`
  opacity: ${(p: any) => p.isVisible ? 1 : 0};
  transform: translateY(${(p: any) => p.isVisible ? '0' : '24px'});
  transition: opacity ${DesignSystem.motion.duration.slow} ${DesignSystem.motion.easing.easeOut},
              transform ${DesignSystem.motion.duration.slow} ${DesignSystem.motion.easing.easeOut};
  transition-delay: ${(p: any) => (p.delay || 0)}ms;
`;

const ScrollReveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay }) => {
  const { ref, isVisible } = useScrollReveal();
  return (
    <RevealSection ref={ref} isVisible={isVisible} delay={delay}>
      {children}
    </RevealSection>
  );
};

// ─── Scroll Progress Bar ──────────────────────────────────────────────────────

const ProgressBar = styled.div<{ width: number }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 3px;
  width: ${(p: any) => p.width}%;
  background: linear-gradient(90deg, ${getColor('primary.700')}, ${getColor('primary.500')});
  z-index: ${DesignSystem.zIndex.toast};
  transition: width 50ms linear;
`;

// ─── Back to Top ──────────────────────────────────────────────────────────────

const BackToTopButton = styled.button<{ visible: boolean }>`
  position: fixed;
  bottom: ${getSpacing('xl')};
  right: ${getSpacing('xl')};
  width: 44px;
  height: 44px;
  border-radius: ${DesignSystem.borderRadius.full};
  background: ${getColor('primary.700')};
  color: ${getColor('neutral.white')};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${getShadow('large')};
  z-index: ${DesignSystem.zIndex.sticky};
  opacity: ${(p: any) => p.visible ? 1 : 0};
  transform: translateY(${(p: any) => p.visible ? '0' : '16px'});
  pointer-events: ${(p: any) => p.visible ? 'auto' : 'none'};
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};

  &:hover {
    background: ${getColor('primary.800')};
    transform: translateY(-2px);
  }
`;

// ─── Layout Shells ────────────────────────────────────────────────────────────

const PageContainer = styled.div`
  background: ${getColor('neutral.white')};
  color: ${getColor('primary.900')};
  font-family: ${DesignSystem.typography.fonts.primary};
  line-height: ${DesignSystem.typography.scale.body.lineHeight};
  min-height: 100vh;
  position: relative;
  overflow-x: clip;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${getSpacing('xl')};
  width: 100%;
  
  @media (max-width: ${DesignSystem.breakpoints.md}) {
    padding: 0 ${getSpacing('lg')};
  }
`;

// ─── Hero Section ─────────────────────────────────────────────────────────────
// Hero now uses shared CinematicHero component

// Pricing pill rendered inside hero
const HeroPricingPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 28px;
  padding: 14px 32px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: 40px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 1.15rem;
  font-weight: 600;
  letter-spacing: 0.01em;
`;

// ─── Quick Stats Bar ──────────────────────────────────────────────────────────

const StatsBar = styled.div`
  background: ${getColor('neutral.white')};
  padding: ${getSpacing('md')} 0;
  position: sticky;
  top: 56px;
  z-index: ${DesignSystem.zIndex.sticky};
  border-bottom: 1px solid transparent;
  background-image: linear-gradient(${getColor('neutral.white')}, ${getColor('neutral.white')}), linear-gradient(90deg, transparent 0%, #CAB96E 50%, transparent 100%);
  background-origin: padding-box, border-box;
  background-clip: padding-box, border-box;

  @media (max-width: ${DesignSystem.breakpoints.md}) {
    top: 56px;
  }
`;

const StatsInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${getSpacing('xl')};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${getSpacing('2xl')};
  flex-wrap: wrap;
  
  @media (max-width: ${DesignSystem.breakpoints.md}) {
    gap: ${getSpacing('lg')};
    padding: 0 ${getSpacing('lg')};
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSpacing('sm')};
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  color: ${getColor('primary.700')};
  font-weight: 500;

  svg {
    width: 16px;
    height: 16px;
    color: ${getColor('primary.500')};
  }

  .stat-value {
    font-weight: 600;
    color: ${getColor('primary.900')};
  }
`;

// ─── Mobile Day Navigation ────────────────────────────────────────────────────

const MobileDayNav = styled.div`
  display: none;
  
  @media (max-width: ${DesignSystem.breakpoints.md}) {
    display: block;
    position: sticky;
    top: 108px;
    z-index: ${DesignSystem.zIndex.sticky};
    background: ${getColor('neutral.white')};
    border-bottom: 1px solid ${getColor('primary.200')};
    padding: ${getSpacing('sm')} 0;
  }
`;

const MobileDayScroller = styled.div`
  display: flex;
  gap: ${getSpacing('sm')};
  overflow-x: auto;
  padding: ${getSpacing('xs')} ${getSpacing('lg')};
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  
  &::-webkit-scrollbar { display: none; }
`;

const MobileDayChip = styled.button<{ isActive: boolean }>`
  flex-shrink: 0;
  scroll-snap-align: start;
  padding: ${getSpacing('xs')} ${getSpacing('lg')};
  border-radius: ${DesignSystem.borderRadius.full};
  border: 1px solid ${(p: any) => p.isActive ? getColor('primary.700') : getColor('primary.300')};
  background: ${(p: any) => p.isActive ? getColor('primary.700') : 'transparent'};
  color: ${(p: any) => p.isActive ? getColor('neutral.white') : getColor('primary.700')};
  font-size: ${DesignSystem.typography.scale.caption.size};
  font-weight: 600;
  font-family: ${DesignSystem.typography.fonts.primary};
  cursor: pointer;
  white-space: nowrap;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};

  &:hover {
    border-color: ${getColor('primary.700')};
    background: ${(p: any) => p.isActive ? getColor('primary.700') : getColor('primary.100')};
  }
`;

// ─── Main Layout ──────────────────────────────────────────────────────────────

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: ${getSpacing('xl')};
  padding: ${getSpacing('xl')} ${getSpacing('lg')};
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${DesignSystem.breakpoints.lg}) {
    grid-template-columns: 220px 1fr;
    gap: ${getSpacing('lg')};
  }
  
  @media (max-width: ${DesignSystem.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${getSpacing('md')};
    padding: ${getSpacing('lg')} ${getSpacing('md')};
  }
`;

// ─── Sidebar ──────────────────────────────────────────────────────────────────

const DaysSidebar = styled.div`
  position: sticky;
  top: 126px;
  height: fit-content;
  background: ${getColor('neutral.white')};
  border-radius: ${DesignSystem.borderRadius.xl};
  border: 1px solid ${getColor('primary.200')};
  padding: ${getSpacing('lg')};
  box-shadow: ${getShadow('subtle')};
  overflow-y: auto;
  max-height: calc(100vh - 200px);

  @media (max-width: ${DesignSystem.breakpoints.md}) {
    display: none;
  }

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${getColor('primary.300')};
    border-radius: 2px;
  }
`;

const SidebarTitle = styled.h3`
  font-size: ${DesignSystem.typography.scale.caption.size};
  font-weight: 600;
  color: ${getColor('primary.800')};
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin: 0 0 ${getSpacing('md')} 0;
  padding-bottom: ${getSpacing('md')};
  border-bottom: 1px solid ${getColor('primary.200')};
`;

const DaysList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('xs')};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 14px;
    top: 12px;
    bottom: 12px;
    width: 2px;
    background: ${getColor('primary.200')};
    border-radius: 1px;
  }
`;

const DayNavItem = styled.button<{ isActive: boolean }>`
  width: 100%;
  text-align: left;
  padding: ${getSpacing('sm')} ${getSpacing('sm')} ${getSpacing('sm')} ${getSpacing('2xl')};
  border: none;
  background: ${(p: any) => p.isActive ? getColor('primary.50') : 'transparent'};
  border-radius: ${DesignSystem.borderRadius.md};
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 10px;
    border-radius: ${DesignSystem.borderRadius.full};
    background: ${(p: any) => p.isActive ? '#CAB96E' : getColor('neutral.white')};
    border: 2px solid ${(p: any) => p.isActive ? '#CAB96E' : getColor('primary.400')};
    transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
    z-index: 1;
  }

  &:hover {
    background: ${getColor('primary.100')};
    &::before { border-color: ${getColor('primary.700')}; }
  }

  .day-number {
    font-size: ${DesignSystem.typography.scale.caption.size};
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${(p: any) => p.isActive ? '#CAB96E' : getColor('primary.500')};
  }

  .day-name {
    font-size: ${DesignSystem.typography.scale.bodySmall.size};
    font-weight: ${(p: any) => p.isActive ? '600' : '400'};
    color: ${getColor('primary.900')};
    margin-top: 2px;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('xl')};
  min-width: 0;
`;

// ─── Accordion ────────────────────────────────────────────────────────────────

const AccordionSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${getSpacing('lg')};
  
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${getSpacing('sm')};
  }
`;

const AccordionSectionTitle = styled.h2`
  font-size: clamp(1.4rem, 3vw, 1.8rem);
  font-weight: 500;
  color: #CAB96E;
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  margin: 0;
  letter-spacing: -0.02em;
  font-style: italic;
  line-height: 1.3;
  text-transform: uppercase;
  
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    font-size: 1.25rem;
  }
`;

const AccordionControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSpacing('sm')};
`;

const ExpandCollapseButton = styled.button<{ variant?: 'expand' | 'collapse' }>`
  display: inline-flex;
  align-items: center;
  gap: ${getSpacing('xs')};
  padding: ${getSpacing('xs')} ${getSpacing('md')};
  font-size: ${DesignSystem.typography.scale.caption.size};
  font-weight: 600;
  font-family: ${DesignSystem.typography.fonts.primary};
  background: ${(p: any) => p.variant === 'expand' ? getColor('primary.700') : 'transparent'};
  color: ${(p: any) => p.variant === 'expand' ? getColor('neutral.white') : getColor('primary.700')};
  border: 1px solid ${(p: any) => p.variant === 'expand' ? getColor('primary.700') : getColor('primary.300')};
  border-radius: ${DesignSystem.borderRadius.full};
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  
  &:hover {
    background: ${(p: any) => p.variant === 'expand' ? getColor('primary.800') : getColor('primary.50')};
    border-color: ${(p: any) => p.variant === 'expand' ? getColor('primary.800') : getColor('primary.500')};
    transform: translateY(-1px);
  }
  
  svg { width: 12px; height: 12px; }
`;

const AccordionContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${DesignSystem.borderRadius.lg};
  border: 1px solid ${getColor('primary.200')};
  overflow: hidden;
  background: ${getColor('neutral.white')};
`;

const AccordionItem = styled.div<{ isExpanded: boolean }>`
  background: ${(p: any) => p.isExpanded ? getColor('primary.50') : getColor('neutral.white')};
  border-bottom: 1px solid ${getColor('primary.200')};
  transition: background ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  scroll-margin-top: 160px;
  
  &:last-child { border-bottom: none; }
`;

const AccordionHeader = styled.div<{ isExpanded: boolean }>`
  padding: ${getSpacing('md')} ${getSpacing('lg')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${getSpacing('md')};
  background: transparent;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  
  &:hover {
    background: ${getColor('primary.100')};
  }
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    padding: ${getSpacing('sm')} ${getSpacing('md')};
    gap: ${getSpacing('sm')};
  }
`;

const AccordionHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSpacing('md')};
  flex: 1;
  min-width: 0;
`;

const DayBadge = styled.div`
  color: ${getColor('neutral.white')};
  background: ${getColor('primary.700')};
  font-size: ${DesignSystem.typography.scale.caption.size};
  font-family: ${DesignSystem.typography.fonts.primary};
  font-weight: 600;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: ${DesignSystem.borderRadius.lg};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  line-height: 1;
  flex-direction: column;
  gap: 1px;

  .day-label {
    font-size: 0.6rem;
    font-weight: 600;
    opacity: 0.8;
  }

  .day-number {
    font-size: 1rem;
    font-weight: 600;
  }
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    width: 36px;
    height: 36px;
    .day-label { font-size: 0.5rem; }
    .day-number { font-size: 0.85rem; }
  }
`;

const DayTitle = styled.div`
  flex: 1;
  min-width: 0;
  
  h3 {
    color: ${getColor('primary.900')};
    font-size: ${DesignSystem.typography.scale.body.size};
    font-weight: 500;
    margin: 0;
    line-height: 1.4;
    font-family: ${DesignSystem.typography.fonts.primary};
  }

  .route-preview {
    font-size: ${DesignSystem.typography.scale.caption.size};
    color: ${getColor('primary.700')};
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: ${getSpacing('xs')};

    svg { width: 12px; height: 12px; }
  }
`;

const ChevronIconWrapper = styled.div<{ isExpanded: boolean }>`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  transform: ${(p: any) => p.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  color: ${getColor('primary.700')};
  flex-shrink: 0;
  border-radius: ${DesignSystem.borderRadius.full};
  background: ${getColor('primary.100')};

  svg { width: 16px; height: 16px; }
`;

const AccordionContentGrid = styled.div<{ isExpanded: boolean }>`
  display: grid;
  grid-template-rows: ${(p: any) => p.isExpanded ? '1fr' : '0fr'};
  transition: grid-template-rows ${DesignSystem.motion.duration.slow} ${DesignSystem.motion.easing.ease};
`;

const AccordionContentInner = styled.div`
  overflow: hidden;
`;

const AccordionInner = styled.div`
  padding: ${getSpacing('lg')};
  border-top: 1px solid ${getColor('primary.200')};
  
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    padding: ${getSpacing('md')};
  }
`;

// ─── Collapse Link ────────────────────────────────────────────────────────────

const CollapseLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${getSpacing('xs')};
  width: 100%;
  padding: ${getSpacing('md')} 0;
  margin-top: ${getSpacing('lg')};
  background: transparent;
  border: none;
  border-top: 1px solid ${getColor('primary.200')};
  color: ${getColor('primary.800')};
  font-size: ${DesignSystem.typography.scale.caption.size};
  font-weight: 500;
  font-family: ${DesignSystem.typography.fonts.primary};
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  
  &:hover {
    color: ${getColor('primary.800')};
  }
  
  svg {
    width: 14px;
    height: 14px;
    transition: transform ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  }
  
  &:hover svg {
    transform: translateY(-2px);
  }
`;

// ─── Day Image Gallery ────────────────────────────────────────────────────────

const DayImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: ${getSpacing('md')};
  margin: ${getSpacing('md')} 0 ${getSpacing('lg')};

  @media (max-width: ${DesignSystem.breakpoints.md}) {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    gap: ${getSpacing('sm')};
    padding-bottom: ${getSpacing('sm')};
    
    &::-webkit-scrollbar { display: none; }
  }
`;

const ImageCard = styled.div`
  position: relative;
  aspect-ratio: 4/3;
  border-radius: ${DesignSystem.borderRadius.lg};
  overflow: hidden;
  cursor: pointer;
  background: linear-gradient(135deg, ${getColor('primary.100')}, ${getColor('accent.100')});
  box-shadow: ${getShadow('subtle')};
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  border: 1px solid ${getColor('primary.200')};

  @media (max-width: ${DesignSystem.breakpoints.md}) {
    min-width: 160px;
    scroll-snap-align: start;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${getShadow('medium')};
    img { transform: scale(1.05); }
    .image-overlay { opacity: 1; }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  }

  .image-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.3) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
    svg { width: 24px; height: 24px; color: ${getColor('neutral.white')}; }
  }

  .image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, ${getColor('primary.50')}, ${getColor('accent.50')});
    svg { width: 28px; height: 28px; color: ${getColor('primary.400')}; }
  }
`;

const GalleryTitle = styled.h4`
  font-size: ${DesignSystem.typography.scale.body.size};
  font-weight: 600;
  color: ${getColor('primary.800')};
  margin: ${getSpacing('lg')} 0 ${getSpacing('sm')} 0;
  display: flex;
  align-items: center;
  gap: ${getSpacing('sm')};

  &::before {
    content: '';
    width: 3px;
    height: 18px;
    background: linear-gradient(180deg, ${getColor('primary.700')}, ${getColor('primary.500')});
    border-radius: 2px;
  }
`;

// ─── Route & Overnight Info ───────────────────────────────────────────────────

const RouteInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${getSpacing('md')};
  margin-bottom: ${getSpacing('lg')};
  padding: ${getSpacing('md')};
  background: ${getColor('primary.50')};
  border-radius: ${DesignSystem.borderRadius.md};
  border: 1px solid ${getColor('primary.200')};
`;

const RouteItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${getSpacing('sm')};
  flex: 1;
  min-width: 200px;
  
  .route-icon {
    width: 32px;
    height: 32px;
    border-radius: ${DesignSystem.borderRadius.md};
    background: ${getColor('primary.200')};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    svg { width: 16px; height: 16px; color: ${getColor('primary.700')}; }
  }
  
  .route-label {
    font-size: ${DesignSystem.typography.scale.caption.size};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${getColor('primary.700')};
    font-weight: 600;
    margin-bottom: 2px;
  }
  
  .route-value {
    font-size: ${DesignSystem.typography.scale.bodySmall.size};
    color: ${getColor('primary.900')};
    font-weight: 500;
    line-height: 1.4;
  }
`;

// ─── Place Cards ──────────────────────────────────────────────────────────────

const PlacesGrid = styled(Grid)`
  margin-bottom: ${getSpacing('xl')};
`;

const PlaceCard = styled.div`
  background: ${getColor('neutral.white')};
  border-radius: ${DesignSystem.borderRadius.lg};
  padding: ${getSpacing('lg')} ${getSpacing('xl')};
  border: 1px solid ${getColor('primary.200')};
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: ${getShadow('subtle')};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, ${getColor('primary.700')}, ${getColor('primary.500')});
    transform: scaleY(0);
    transition: transform ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
    transform-origin: bottom;
  }
  
  &:hover {
    transform: translateX(6px);
    box-shadow: ${getShadow('medium')};
    border-color: ${getColor('primary.400')};
    &::before { transform: scaleY(1); }
  }
  
  .place-name {
    font-weight: 600;
    color: ${getColor('primary.900')};
    margin-bottom: ${getSpacing('xs')};
    font-size: ${DesignSystem.typography.scale.body.size};
    font-family: ${DesignSystem.typography.fonts.primary};
    line-height: 1.3;
  }
  
  .place-type {
    display: inline-block;
    background: ${getColor('primary.200')};
    color: ${getColor('primary.700')};
    padding: 2px ${getSpacing('sm')};
    border-radius: ${DesignSystem.borderRadius.full};
    font-size: ${DesignSystem.typography.scale.caption.size};
    font-weight: 600;
    font-family: ${DesignSystem.typography.fonts.secondary};
    margin-bottom: ${getSpacing('sm')};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .place-description {
    color: ${getColor('neutral.gray.600')};
    font-size: ${DesignSystem.typography.scale.bodySmall.size};
    font-family: ${DesignSystem.typography.fonts.secondary};
    line-height: 1.7;
  }
`;

// ─── Section Divider & Subsection ─────────────────────────────────────────────

// Gold accent divider between major sections
const SectionDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${getSpacing('xl')};
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    max-width: 120px;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      #CAB96E 50%,
      transparent 100%
    );
  }
  
  & > span {
    color: #CAB96E;
    font-size: 0.5rem;
    margin: 0 ${getSpacing('md')};
    opacity: 0.7;
  }
`;

// Gold section label (small caps above section titles)
const SectionLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #CAB96E;
  display: block;
  margin-bottom: ${getSpacing('xs')};
`;

const SubsectionTitle = styled.h4`
  color: ${getColor('primary.900')};
  font-size: ${DesignSystem.typography.scale.body.size};
  font-weight: 600;
  font-family: ${DesignSystem.typography.fonts.primary};
  margin-bottom: ${getSpacing('md')};
  display: flex;
  align-items: center;
  gap: ${getSpacing('sm')};

  &::before {
    content: '';
    width: 3px;
    height: 20px;
    background: linear-gradient(180deg, ${getColor('primary.700')}, ${getColor('primary.500')});
    border-radius: 2px;
  }
`;

// ─── Activity Badges ──────────────────────────────────────────────────────────

const ActivityBadgeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${getSpacing('sm')};
`;

const ActivityBadge = styled.span`
  background: ${getColor('primary.100')};
  color: ${getColor('primary.800')};
  padding: ${getSpacing('xs')} ${getSpacing('md')};
  border-radius: ${DesignSystem.borderRadius.full};
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  font-weight: 500;
  font-family: ${DesignSystem.typography.fonts.secondary};
  text-transform: capitalize;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  border: 1px solid ${getColor('primary.200')};
  
  &:hover {
    background: ${getColor('primary.200')};
    transform: translateY(-2px);
    box-shadow: ${getShadow('subtle')};
  }
`;

// ─── Info Sections ────────────────────────────────────────────────────────────

const InfoSection = styled.div`
  background: ${getColor('neutral.white')};
  border-radius: ${DesignSystem.borderRadius.lg};
  padding: ${getSpacing('xl')};
  border: 1px solid ${getColor('primary.200')};
  box-shadow: ${getShadow('subtle')};
`;

const InfoSectionTitle = styled.h3`
  font-size: ${DesignSystem.typography.scale.h5.size};
  font-weight: ${DesignSystem.typography.scale.h5.weight};
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('md')} 0;
  display: flex;
  align-items: center;
  gap: ${getSpacing('sm')};
  svg { width: 20px; height: 20px; color: ${getColor('primary.600')}; }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${getSpacing('lg')};
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const HighlightsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('xs')};
`;

const HighlightItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${getSpacing('sm')};
  padding: ${getSpacing('xs')} 0;
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  color: ${getColor('primary.800')};
  line-height: 1.6;
  svg { width: 16px; height: 16px; color: ${getColor('semantic.success')}; flex-shrink: 0; margin-top: 2px; }
`;

const PermitCard = styled.div`
  padding: ${getSpacing('sm')} 0;
`;

const PermitRow = styled.div`
  display: flex;
  padding: ${getSpacing('sm')} 0;
  border-bottom: 1px solid ${getColor('primary.100')};
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  &:last-child { border-bottom: none; }
`;

const PermitLabel = styled.span`
  font-weight: 500;
  font-style: italic;
  color: ${getColor('primary.800')};
  min-width: 120px;
  display: inline-block;
`;

const PackingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${getSpacing('xs')};
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const PackingItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSpacing('sm')};
  padding: ${getSpacing('xs')} ${getSpacing('sm')};
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  color: ${getColor('primary.800')};
  svg { width: 6px; height: 6px; color: ${getColor('primary.700')}; flex-shrink: 0; fill: ${getColor('primary.700')}; }
`;

const HealthTipItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${getSpacing('sm')};
  padding: ${getSpacing('xs')} 0;
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  color: ${getColor('primary.800')};
  line-height: 1.6;
  svg { width: 14px; height: 14px; color: ${getColor('primary.700')}; flex-shrink: 0; margin-top: 3px; }
`;

// ─── Activity Cards (for object-type activities) ──────────────────────────────

const ActivityCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${getSpacing('md')};
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ActivityCard = styled.div`
  background: ${getColor('neutral.white')};
  border: 1px solid ${getColor('primary.200')};
  border-radius: ${DesignSystem.borderRadius.lg};
  padding: ${getSpacing('lg')};
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 3px; height: 100%;
    background: #CAB96E;
  }

  &:hover {
    box-shadow: ${getShadow('soft')};
    border-color: ${getColor('primary.400')};
    transform: translateY(-2px);
  }
`;

const ActivityCardName = styled.h5`
  font-size: ${DesignSystem.typography.scale.body.size};
  font-weight: 600;
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('sm')} 0;
  font-family: ${DesignSystem.typography.fonts.primary};
  line-height: 1.3;
`;

const ActivityCardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${getSpacing('md')};
  margin-bottom: ${getSpacing('sm')};
  font-size: ${DesignSystem.typography.scale.caption.size};
  color: ${getColor('primary.700')};

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  svg { width: 12px; height: 12px; }
`;

const ActivityCardDesc = styled.p`
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  color: ${getColor('primary.800')};
  line-height: 1.6;
  margin: 0;
`;

const ActivityCardBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${getColor('primary.100')};
  color: ${getColor('primary.800')};
  padding: 2px ${getSpacing('sm')};
  border-radius: ${DesignSystem.borderRadius.full};
  font-size: ${DesignSystem.typography.scale.caption.size};
  font-weight: 500;
  margin-top: ${getSpacing('sm')};

  svg { width: 10px; height: 10px; }
`;

// ─── View More Toggle ─────────────────────────────────────────────────────────

const ViewMoreToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${getSpacing('xs')};
  background: none;
  border: none;
  color: ${getColor('primary.700')};
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  font-weight: 600;
  font-family: ${DesignSystem.typography.fonts.primary};
  cursor: pointer;
  padding: ${getSpacing('sm')} 0;
  margin-top: ${getSpacing('sm')};
  transition: color ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};

  &:hover {
    color: ${getColor('primary.900')};
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  }
`;

// ─── Pricing ──────────────────────────────────────────────────────────────────

const PricingSection = styled.div`
  background: ${getColor('neutral.white')};
  border-radius: ${DesignSystem.borderRadius.xl};
  padding: ${getSpacing('xl')};
  border: 1px solid ${getColor('primary.200')};
`;

const PricingTitle = styled.h3`
  font-size: ${DesignSystem.typography.scale.h4.size};
  font-weight: ${DesignSystem.typography.scale.h4.weight};
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('md')} 0;
  display: flex;
  align-items: center;
  gap: ${getSpacing('sm')};
  svg { width: 22px; height: 22px; color: ${getColor('primary.600')}; }
`;

const CarCategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${getSpacing('md')};
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const CarCategoryCard = styled.div`
  background: ${getColor('neutral.white')};
  border-radius: ${DesignSystem.borderRadius.lg};
  padding: ${getSpacing('xl')};
  border: 1px solid ${getColor('primary.200')};
  text-align: center;
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${getColor('primary.700')}, ${getColor('primary.500')});
  }
  
  &:hover {
    transform: translateY(-6px);
    box-shadow: ${getShadow('large')};
  }
  
  .category-label {
    display: inline-block;
    background: ${getColor('primary.700')};
    color: ${getColor('neutral.white')};
    padding: ${getSpacing('xs')} ${getSpacing('md')};
    border-radius: ${DesignSystem.borderRadius.full};
    font-size: ${DesignSystem.typography.scale.caption.size};
    font-weight: 700;
    margin-bottom: ${getSpacing('md')};
  }
  
  .vehicles {
    font-size: ${DesignSystem.typography.scale.bodySmall.size};
    color: ${getColor('primary.800')};
    margin-bottom: ${getSpacing('md')};
    line-height: 1.5;
  }
  
  .price {
    font-size: ${DesignSystem.typography.scale.h3.size};
    font-weight: 500;
    color: ${getColor('primary.900')};
    font-family: ${DesignSystem.typography.fonts.display};
  }
  
  .price-note {
    font-size: ${DesignSystem.typography.scale.caption.size};
    color: ${getColor('primary.700')};
    margin-top: ${getSpacing('xs')};
  }
`;

const IncludesExcludes = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${getSpacing('lg')};
  margin-top: ${getSpacing('lg')};
  @media (max-width: ${DesignSystem.breakpoints.sm}) { grid-template-columns: 1fr; }
`;

const IncludesBox = styled.div`
  h4 {
    color: ${getColor('semantic.success')};
    font-size: ${DesignSystem.typography.scale.body.size};
    font-weight: 600;
    margin: 0 0 ${getSpacing('sm')} 0;
    display: flex;
    align-items: center;
    gap: ${getSpacing('xs')};
    svg { width: 18px; height: 18px; }
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    li {
      padding: ${getSpacing('xs')} 0;
      font-size: ${DesignSystem.typography.scale.bodySmall.size};
      color: ${getColor('primary.800')};
      display: flex;
      align-items: flex-start;
      gap: ${getSpacing('xs')};
      svg { width: 14px; height: 14px; color: ${getColor('semantic.success')}; flex-shrink: 0; margin-top: 2px; }
    }
  }
`;

const ExcludesBox = styled.div`
  h4 {
    color: ${getColor('semantic.error')};
    font-size: ${DesignSystem.typography.scale.body.size};
    font-weight: 600;
    margin: 0 0 ${getSpacing('sm')} 0;
    display: flex;
    align-items: center;
    gap: ${getSpacing('xs')};
    svg { width: 18px; height: 18px; }
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    li {
      padding: ${getSpacing('xs')} 0;
      font-size: ${DesignSystem.typography.scale.bodySmall.size};
      color: ${getColor('primary.800')};
      display: flex;
      align-items: flex-start;
      gap: ${getSpacing('xs')};
      svg { width: 14px; height: 14px; color: ${getColor('semantic.error')}; flex-shrink: 0; margin-top: 2px; }
    }
  }
`;

// ─── Car Pricing Table ────────────────────────────────────────────────────────

const PricingTableWrapper = styled.div`
  margin-top: ${getSpacing('xl')};
`;

const GSTNotice = styled.p`
  font-size: 0.8rem;
  font-style: italic;
  font-weight: 400;
  color: ${getColor('primary.600')};
  margin: 0 0 ${getSpacing('lg')} 0;
  letter-spacing: 0.2px;
  line-height: 1.5;
`;

const PivotTableContainer = styled.div`
  border-radius: ${DesignSystem.borderRadius.lg};
  border: 1px solid ${getColor('primary.200')};
  overflow: hidden;
  margin-bottom: ${getSpacing('xl')};
  box-shadow: none;
`;

const PivotTableTitle = styled.h4`
  font-size: ${DesignSystem.typography.scale.body.size};
  font-weight: 600;
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('md')} 0;
  display: flex;
  align-items: center;
  gap: ${getSpacing('sm')};
  font-family: ${DesignSystem.typography.fonts.primary};
  
  &::before {
    content: '';
    width: 3px;
    height: 18px;
    background: linear-gradient(180deg, ${getColor('primary.700')}, ${getColor('primary.500')});
    border-radius: 2px;
  }
`;

const PricingTableScrollWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: ${getColor('primary.300')} transparent;
  
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: ${getColor('primary.300')}; border-radius: 2px; }
`;

const PivotTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 480px;
  font-family: ${DesignSystem.typography.fonts.primary};
`;

const PivotTHead = styled.thead`
  background: rgba(202, 185, 110, 0.15);
  
  th {
    padding: ${getSpacing('md')} ${getSpacing('lg')};
    text-align: left;
    font-size: ${DesignSystem.typography.scale.caption.size};
    font-weight: 700;
    color: ${getColor('primary.900')};
    text-transform: uppercase;
    letter-spacing: 1px;
    white-space: nowrap;
    
    &:not(:first-child) {
      text-align: center;
    }
  }
`;

const PivotTBody = styled.tbody`
  tr {
    background: ${getColor('neutral.white')};
    transition: background ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
    
    &:hover {
      background: ${getColor('neutral.white')};
    }
    
    td {
      padding: ${getSpacing('sm')} ${getSpacing('lg')};
      font-size: 0.9rem;
      color: ${getColor('primary.900')};
      border-bottom: 1px solid ${getColor('primary.100')};
      white-space: nowrap;
      font-weight: 400;
      line-height: 1.6;
      
      &:not(:first-child) {
        text-align: center;
        font-weight: 500;
        font-variant-numeric: tabular-nums;
      }
    }
    
    &:last-child td {
      border-bottom: none;
    }
  }
`;

const BestValueCell = styled.td`
  && {
    background: ${getColor('neutral.white')};
    font-weight: 700 !important;
    color: ${getColor('primary.700')};
    position: relative;
  }
`;

const GroupSizeLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${getSpacing('xs')};
  font-weight: 600;
  
  svg { width: 14px; height: 14px; color: ${getColor('primary.600')}; }
`;

// ─── Tier Detail Sections ─────────────────────────────────────────────────────

const TierSectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('xl')};
`;

const TierSectionTitle = styled.h4`
  font-size: ${DesignSystem.typography.scale.body.size};
  font-weight: 600;
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('md')} 0;
  display: flex;
  align-items: center;
  gap: ${getSpacing('sm')};
  font-family: ${DesignSystem.typography.fonts.primary};
  
  &::before {
    content: '';
    width: 3px;
    height: 18px;
    background: linear-gradient(180deg, ${getColor('primary.700')}, ${getColor('primary.500')});
    border-radius: 2px;
  }
`;

const TierCard = styled.div`
  border-radius: ${DesignSystem.borderRadius.lg};
  border: 1px solid ${getColor('primary.200')};
  overflow: hidden;
  background: ${getColor('neutral.white')};
`;

const TierLabel = styled.h5`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${getColor('primary.900')};
  margin: 0;
  padding: ${getSpacing('md')} ${getSpacing('lg')};
  border-bottom: 1px solid ${getColor('primary.200')};
  font-family: ${DesignSystem.typography.fonts.primary};
  background: ${getColor('neutral.white')};
`;

const TierTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${DesignSystem.typography.fonts.primary};
  
  thead {
    background: ${getColor('neutral.white')};
    th {
      padding: ${getSpacing('sm')} ${getSpacing('md')};
      text-align: left;
      font-size: ${DesignSystem.typography.scale.caption.size};
      font-weight: 700;
      color: ${getColor('primary.800')};
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border-bottom: 1px solid ${getColor('primary.200')};
      white-space: nowrap;
    }
  }
  
  tbody {
    tr {
      background: ${getColor('neutral.white')};
      transition: background ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
      
      &:hover {
        background: ${getColor('neutral.white')};
      }
      
      td {
        padding: ${getSpacing('sm')} ${getSpacing('md')};
        font-size: 0.9rem;
        color: ${getColor('primary.900')};
        border-bottom: 1px solid ${getColor('primary.100')};
        font-weight: 400;
        line-height: 1.6;
        
        &.price-cell {
          font-weight: 600;
          font-variant-numeric: tabular-nums;
          color: ${getColor('primary.900')};
        }
      }
      
      &:last-child td {
        border-bottom: none;
      }
    }
  }
`;

// ─── CTA ──────────────────────────────────────────────────────────────────────

const CTASection = styled.div`
  background: ${getColor('neutral.white')};
  color: ${getColor('primary.900')};
  padding: ${getSpacing('2xl')} ${getSpacing('xl')};
  text-align: center;
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    padding: ${getSpacing('xl')} ${getSpacing('md')};
  }
`;

const CTAHeading = styled.h2`
  font-size: clamp(1.6rem, 3vw, 2.4rem);
  font-weight: 500;
  color: #CAB96E;
  margin: 0 0 ${getSpacing('sm')} 0;
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  letter-spacing: -0.02em;
  font-style: italic;
  line-height: 1.3;
  text-transform: uppercase;
`;

const CTASubtext = styled.p`
  font-size: 0.9rem;
  color: ${getColor('primary.600')};
  margin: 4px 0 ${getSpacing('xl')} 0;
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.01em;
`;

const CTAButton = styled.button`
  padding: 14px 32px;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  background: #CAB96E;
  border-radius: 50px;
  border: 1px solid #CAB96E;
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(101, 90, 71, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    background: #b8a75e;
    border-color: #b8a75e;
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 20px rgba(101, 90, 71, 0.3);
  }
  
  &:active { transform: translateY(0) scale(0.98); }
`;

// ─── Sticky Book Now Button (removed — functionality merged into CTA) ─────────

// ─── Activity Parser Helper ───────────────────────────────────────────────────

/** Parse a stringified Python dict like "{'name': 'foo', 'time': '...'}" into a JS object */
const parseActivityString = (str: string): Record<string, string> | null => {
  if (typeof str !== 'string' || !str.trim().startsWith('{')) return null;
  try {
    // Convert Python-style single-quoted dict to valid JSON
    const jsonStr = str
      .replace(/'/g, '"')          // single → double quotes
      .replace(/True/g, 'true')    // Python bools
      .replace(/False/g, 'false')
      .replace(/None/g, 'null');
    return JSON.parse(jsonStr);
  } catch {
    return null;
  }
};

/** Check if activity is or contains a dict-like structure */
const isActivityObject = (activity: any): boolean => {
  if (typeof activity === 'object' && activity !== null) return true;
  if (typeof activity === 'string' && activity.trim().startsWith('{') && activity.includes("'name'")) return true;
  if (typeof activity === 'string' && activity.trim().startsWith('{') && activity.includes("'Name'")) return true;
  return false;
};

/** Get parsed activity object from either a real object or a stringified dict */
const getActivityData = (activity: any): Record<string, string> | null => {
  if (typeof activity === 'object' && activity !== null) return activity;
  if (typeof activity === 'string') return parseActivityString(activity);
  return null;
};

// ─── Loading & Error ──────────────────────────────────────────────────────────

const SkeletonPulse = styled.div`
  background: linear-gradient(90deg, ${getColor('primary.100')} 25%, ${getColor('primary.200')} 50%, ${getColor('primary.100')} 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease infinite;
  border-radius: ${DesignSystem.borderRadius.md};
`;

const LoadingPage = styled.div`
  min-height: 100vh;
  background: ${getColor('neutral.white')};
`;

const SkeletonHero = styled(SkeletonPulse)`
  height: 400px;
  border-radius: 0;
`;

const SkeletonContent = styled.div`
  max-width: 1200px;
  margin: ${getSpacing('2xl')} auto;
  padding: 0 ${getSpacing('xl')};
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: ${getSpacing('2xl')};
  @media (max-width: ${DesignSystem.breakpoints.md}) { grid-template-columns: 1fr; }
`;

const SkeletonSidebar = styled(SkeletonPulse)`
  height: 300px;
  border-radius: ${DesignSystem.borderRadius.lg};
  @media (max-width: ${DesignSystem.breakpoints.md}) { display: none; }
`;

const SkeletonCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('md')};
`;

const SkeletonCard = styled(SkeletonPulse)<{ height?: string }>`
  height: ${(p: any) => p.height || '80px'};
  border-radius: ${DesignSystem.borderRadius.lg};
`;

const ErrorPage = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${getSpacing('2xl')};
`;

const ErrorCard = styled.div`
  background: ${getColor('neutral.white')};
  border: 1px solid ${getColor('primary.200')};
  border-left: 4px solid ${getColor('primary.700')};
  border-radius: ${DesignSystem.borderRadius.lg};
  padding: ${getSpacing('2xl')};
  max-width: 480px;
  text-align: center;
  box-shadow: ${getShadow('subtle')};

  h3 {
    color: ${getColor('primary.900')};
    font-size: ${DesignSystem.typography.scale.h4.size};
    font-weight: ${DesignSystem.typography.scale.h4.weight};
    margin: 0 0 ${getSpacing('sm')} 0;
  }

  p {
    color: ${getColor('primary.800')};
    font-size: ${DesignSystem.typography.scale.bodySmall.size};
    margin: 0 0 ${getSpacing('lg')} 0;
    line-height: 1.6;
  }
`;

const RetryButton = styled.button`
  padding: ${getSpacing('sm')} ${getSpacing('xl')};
  background: ${getColor('primary.700')};
  color: ${getColor('neutral.white')};
  border: none;
  border-radius: ${DesignSystem.borderRadius.full};
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  font-weight: 600;
  font-family: ${DesignSystem.typography.fonts.primary};
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  margin-right: ${getSpacing('sm')};
  &:hover { background: ${getColor('primary.800')}; transform: translateY(-1px); }
`;

const BackLink = styled.button`
  padding: ${getSpacing('sm')} ${getSpacing('xl')};
  background: transparent;
  color: ${getColor('primary.700')};
  border: 1px solid ${getColor('primary.300')};
  border-radius: ${DesignSystem.borderRadius.full};
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  font-weight: 500;
  font-family: ${DesignSystem.typography.fonts.primary};
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  &:hover { border-color: ${getColor('primary.500')}; background: ${getColor('primary.50')}; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const DetailedItineraryPage: React.FC<DetailedItineraryPageProps> = ({
  state,
  itineraryId,
  onBack,
  onNavigateTo
}: DetailedItineraryPageProps) => {
  const [itinerary, setItinerary] = useState<ItineraryDetails | null>(null);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isBookingFormOpen, setIsBookingFormOpen] = useState(false);
  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);
  const [showAllPacking, setShowAllPacking] = useState(false);
  const [showAllHealthTips, setShowAllHealthTips] = useState(false);
  const [showAllIncludes, setShowAllIncludes] = useState(false);
  const [showAllExcludes, setShowAllExcludes] = useState(false);

  // ─── BookingForm history integration ──────────────────────────────────────
  const openBookingForm = useCallback(() => {
    setIsEnquiryOpen(true);
    window.history.pushState(
      { page: 'detailed-itinerary', data: { itineraryId, state }, bookingOpen: true },
      '',
      window.location.href
    );
  }, [itineraryId, state]);

  useEffect(() => {
    const handlePopState = () => {
      if (isEnquiryOpen) {
        setIsEnquiryOpen(false);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isEnquiryOpen]);

  // ─── Auto-popup EnquiryDialog after 20s (once per session) ────────────────
  useEffect(() => {
    if (sessionStorage.getItem('enquiry_dialog_shown')) return;
    const timer = setTimeout(() => {
      setIsEnquiryOpen(true);
      sessionStorage.setItem('enquiry_dialog_shown', '1');
    }, 20000);
    return () => clearTimeout(timer);
  }, []);

  const [sampleImages, setSampleImages] = useState<string[]>([]);
  const { progress, showBackToTop } = useScrollProgress();
  const activeScrollDay = useScrollSpy(itinerary?.days?.length || 0);

  // ─── Header dropdown data ─────────────────────────────────────────────────
  const { destinationItineraries, trekkingItineraries, bikeItineraries } = useHeaderItineraries();

  // ─── Scroll to top on page load ───────────────────────────────────────────
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [itineraryId, state]);

  useEffect(() => {
    const fetchItineraryDetails = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const details = await ItineraryAPI.getItineraryDetails(itineraryId, state);
        setItinerary(details);
      } catch (err) {
        setError(`Failed to load itinerary details: ${err instanceof Error ? err.message : 'Unknown error'}`);
        console.error('Error fetching itinerary details:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchItineraryDetails();
  }, [itineraryId, state]);

  // Fetch shared sample images (central pool for all itineraries)
  useEffect(() => {
    ImageAPI.fetchSampleImages().then(setSampleImages);
  }, []);

  // Derive hero image — prefer landingImage, fallback to day images, then sample images
  const heroImage = (() => {
    // Prefer dedicated landing image (WebP)
    if (itinerary?.landingImage) {
      return `${API_BASE_URL}/api/images/${itinerary.landingImage}`;
    }
    // Try to get image from itinerary day data
    if (itinerary?.days) {
      for (const day of itinerary.days) {
        if (day.images && day.images.length > 0) {
          return day.images[0];
        }
      }
    }
    // Fallback to sample images pool
    return sampleImages.length > 0 ? sampleImages[0] : undefined;
  })();

  const toggleDay = useCallback((dayNumber: number) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dayNumber)) { next.delete(dayNumber); } else { next.add(dayNumber); }
      return next;
    });
  }, []);

  const scrollToDay = useCallback((dayNumber: number) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      next.add(dayNumber);
      return next;
    });
    setTimeout(() => {
      const el = document.getElementById(`day-${dayNumber}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);



  // ── Loading ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <LoadingPage>
        <AppHeader
          onNavigateTo={onNavigateTo}
          currentPage="detailed-itinerary"
          transparent
          destinationItineraries={destinationItineraries}
          trekkingItineraries={trekkingItineraries}
          bikeItineraries={bikeItineraries}
        />
        <SkeletonHero />
        <SkeletonContent>
          <SkeletonSidebar />
          <SkeletonCards>
            <SkeletonCard height="60px" />
            <SkeletonCard height="100px" />
            <SkeletonCard height="100px" />
            <SkeletonCard height="100px" />
            <SkeletonCard height="80px" />
          </SkeletonCards>
        </SkeletonContent>
      </LoadingPage>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────

  if (error || !itinerary) {
    return (
      <PageContainer>
        <AppHeader
          onNavigateTo={onNavigateTo}
          currentPage="detailed-itinerary"
          transparent
          destinationItineraries={destinationItineraries}
          trekkingItineraries={trekkingItineraries}
          bikeItineraries={bikeItineraries}
        />
        <ErrorPage>
          <ErrorCard>
            <h3>Unable to Load Itinerary</h3>
            <p>{error || 'The itinerary you are looking for could not be found.'}</p>
            <RetryButton onClick={() => window.location.reload()}>Try Again</RetryButton>
            <BackLink onClick={onBack}>Back to Itineraries</BackLink>
          </ErrorCard>
        </ErrorPage>
      </PageContainer>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const placesCount = itinerary.all_specific_places?.length || 0;

  return (
    <PageContainer>
      <ProgressBar width={progress} />
      <AppHeader
        onNavigateTo={onNavigateTo}
        currentPage="detailed-itinerary"
        transparent
        destinationItineraries={destinationItineraries}
        trekkingItineraries={trekkingItineraries}
        bikeItineraries={bikeItineraries}
      />

      {/* Hero */}
      <CinematicHero
        bgImage={heroImage}
        height="100vh"
        mobileHeight="100vh"
        brightness={1.15}
        contrast={1.1}
        title={<>{itinerary.name}</>}
        subtitle={itinerary.description || "Explore each day's carefully crafted itinerary with handpicked destinations and unforgettable experiences"}
        tagline={<>
          {itinerary.duration || itinerary.days.length} Days
          <span className="separator">·</span>
          {placesCount} Places
          {itinerary.bestTimeToVisit && (<>
            <span className="separator">·</span>
            {typeof itinerary.bestTimeToVisit === 'string' ? itinerary.bestTimeToVisit : (itinerary.bestTimeToVisit as any)?.primarySeason || 'Seasonal'}
          </>)}
          {itinerary.totalDistance && (<>
            <span className="separator">·</span>
            {itinerary.totalDistance}
          </>)}
        </>}
        pricingLabel={itinerary.packagePricing ? (
          <HeroPricingPill>
            <IndianRupee style={{ width: 18, height: 18, opacity: 0.85 }} />
            <span style={{ opacity: 0.8, fontWeight: 400, fontSize: '0.95rem' }}>Starting from</span>
            <span style={{ fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-0.02em' }}>{'\u20B9'}{itinerary.packagePricing.startingFrom.toLocaleString('en-IN')}</span>
            <span style={{ opacity: 0.7, fontWeight: 400, fontSize: '0.85rem' }}>per person</span>
          </HeroPricingPill>
        ) : undefined}
        ctaText="Book This Journey"
        onCtaClick={openBookingForm}
      />

      {/* Stats Bar */}
      <StatsBar>
        <StatsInner>
          <StatItem><Calendar /><span><span className="stat-value">{itinerary.duration || itinerary.days.length}</span> Days</span></StatItem>
          {placesCount > 0 && (<StatItem><MapPin /><span><span className="stat-value">{placesCount}</span> Places</span></StatItem>)}
          {itinerary.bestTimeToVisit && (<StatItem><Mountain /><span>{typeof itinerary.bestTimeToVisit === 'string' ? itinerary.bestTimeToVisit : (itinerary.bestTimeToVisit as any)?.primarySeason || 'Seasonal'}</span></StatItem>)}
          {itinerary.totalDistance && (<StatItem><Route /><span>{itinerary.totalDistance}</span></StatItem>)}
        </StatsInner>
      </StatsBar>

      {/* Mobile Day Nav */}
      <MobileDayNav>
        <MobileDayScroller>
          {itinerary.days.map((day: any) => (
            <MobileDayChip key={day.day} isActive={activeScrollDay === day.day} onClick={() => scrollToDay(day.day)}>
              Day {day.day}
            </MobileDayChip>
          ))}
        </MobileDayScroller>
      </MobileDayNav>

      {/* Main */}
      <MainLayout>
        <DaysSidebar>
          <SidebarTitle>Days Guide</SidebarTitle>
          <DaysList>
            {itinerary.days.map((day: any) => (
              <DayNavItem key={day.day} isActive={activeScrollDay === day.day} onClick={() => scrollToDay(day.day)}>
                <div className="day-number">Day {day.day}</div>
                <div className="day-name">{day.title}</div>
              </DayNavItem>
            ))}
          </DaysList>
        </DaysSidebar>

        <ContentWrapper>
          {/* Highlights */}
          {itinerary.highlights && itinerary.highlights.length > 0 && (
            <ScrollReveal>
              <InfoSection>
                <InfoSectionTitle><Sparkles /> Trip Highlights</InfoSectionTitle>
                <HighlightsList>
                  {itinerary.highlights.map((h: string, i: number) => (<HighlightItem key={i}><Check />{h}</HighlightItem>))}
                </HighlightsList>
              </InfoSection>
            </ScrollReveal>
          )}

          {/* Day Accordion */}
          <SectionDivider><span>◆</span></SectionDivider>
          <ScrollReveal>
            <SectionLabel>Your Journey</SectionLabel>
            <AccordionSectionHeader>
              <AccordionSectionTitle>{itinerary.duration || itinerary.days.length} Days Itinerary</AccordionSectionTitle>
              <AccordionControlsContainer>
                {expandedDays.size < itinerary.days.length ? (
                  <ExpandCollapseButton variant="expand" onClick={() => setExpandedDays(new Set(itinerary.days.map((d: any) => d.day)))}>
                    <Maximize2 /> Expand All
                  </ExpandCollapseButton>
                ) : (
                  <ExpandCollapseButton variant="collapse" onClick={() => setExpandedDays(new Set())}>
                    <Minimize2 /> Collapse All
                  </ExpandCollapseButton>
                )}
              </AccordionControlsContainer>
            </AccordionSectionHeader>
            
            <AccordionContainer>
              {itinerary.days.map((day: any) => {
                const isExpanded = expandedDays.has(day.day);
                const dayNumber = day.dayLabel ? day.dayLabel.replace('Day ', '') : day.day;
                
                return (
                  <AccordionItem key={day.day} isExpanded={isExpanded} id={`day-${day.day}`}>
                    <AccordionHeader isExpanded={isExpanded} onClick={() => toggleDay(day.day)}>
                      <AccordionHeaderContent>
                        <DayBadge>
                          <span className="day-label">Day</span>
                          <span className="day-number">{dayNumber}</span>
                        </DayBadge>
                        <DayTitle>
                          <h3>{day.title}</h3>
                          {!isExpanded && day.route && (
                            <div className="route-preview"><MapPin /> {day.route}</div>
                          )}
                        </DayTitle>
                      </AccordionHeaderContent>
                      <ChevronIconWrapper isExpanded={isExpanded}><ChevronDown /></ChevronIconWrapper>
                    </AccordionHeader>

                    <AccordionContentGrid isExpanded={isExpanded}>
                      <AccordionContentInner>
                        <AccordionInner>
                          {(day.route || day.overnight) && (
                            <RouteInfo>
                              {day.route && (
                                <RouteItem>
                                  <div className="route-icon"><MapPin /></div>
                                  <div>
                                    <div className="route-label">Route</div>
                                    <div className="route-value">{day.route}</div>
                                  </div>
                                </RouteItem>
                              )}
                              {day.overnight && (
                                <RouteItem>
                                  <div className="route-icon"><Moon /></div>
                                  <div>
                                    <div className="route-label">Overnight Stay</div>
                                    <div className="route-value">{day.overnight}</div>
                                  </div>
                                </RouteItem>
                              )}
                            </RouteInfo>
                          )}

                          {(day.tourType || day.description) && (
                            <div style={{ marginBottom: getSpacing('lg') }}>
                              {day.tourType && (
                                <Badge style={{ background: getColor('primary.700'), color: getColor('neutral.white'), marginBottom: getSpacing('sm'), textTransform: 'capitalize' }}>
                                  {day.tourType}
                                </Badge>
                              )}
                              {day.description && (
                                <BodyText style={{ color: getColor('primary.800'), fontSize: DesignSystem.typography.scale.bodySmall.size, lineHeight: 1.7, marginTop: day.tourType ? getSpacing('sm') : '0' }}>
                                  {day.description}
                                </BodyText>
                              )}
                            </div>
                          )}

                          {day.places && day.places.length > 0 && (
                            <>
                              <SubsectionTitle>Places to Visit</SubsectionTitle>
                              <PlacesGrid cols={1} gap="sm" responsiveCols={{ md: 2 }}>
                                {day.places.map((place: any, index: number) => (
                                  <PlaceCard key={index}>
                                    <div className="place-type">{place.type}</div>
                                    <div className="place-name">{place.name}</div>
                                    <div className="place-description">{place.description}</div>
                                  </PlaceCard>
                                ))}
                              </PlacesGrid>
                              {day.activities && day.activities.length > 0 && <SectionDivider />}
                            </>
                          )}

                          {day.activities && day.activities.length > 0 && (
                            <>
                              <SubsectionTitle>Activities & Experiences</SubsectionTitle>
                              {day.activities.some((a: any) => isActivityObject(a)) ? (
                                <ActivityCardsGrid>
                                  {day.activities.map((activity: any, index: number) => {
                                    const parsed = getActivityData(activity);
                                    if (!parsed) {
                                      return <ActivityBadge key={index}>{String(activity)}</ActivityBadge>;
                                    }
                                    const name = parsed.name || parsed.Name || '';
                                    const time = parsed.time || parsed.Time || '';
                                    const duration = parsed.duration || parsed.Duration || '';
                                    const description = parsed.description || parsed.Description || '';
                                    const altitude = parsed.altitude || parsed.Altitude || '';
                                    const distance = parsed.distance || parsed.Distance || '';
                                    return (
                                      <ActivityCard key={index}>
                                        <ActivityCardName>{name}</ActivityCardName>
                                        {(time || duration) && (
                                          <ActivityCardMeta>
                                            {time && <span><Clock size={12} />{time}</span>}
                                            {duration && <span><Route size={12} />{duration}</span>}
                                          </ActivityCardMeta>
                                        )}
                                        {description && <ActivityCardDesc>{description}</ActivityCardDesc>}
                                        {(altitude || distance) && (
                                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                                            {altitude && <ActivityCardBadge><Mountain size={10} />{altitude}</ActivityCardBadge>}
                                            {distance && <ActivityCardBadge><MapPin size={10} />{distance}</ActivityCardBadge>}
                                          </div>
                                        )}
                                      </ActivityCard>
                                    );
                                  })}
                                </ActivityCardsGrid>
                              ) : (
                                <ActivityBadgeContainer>
                                  {day.activities.map((activity: any, index: number) => (
                                    <ActivityBadge key={index}>{String(activity)}</ActivityBadge>
                                  ))}
                                </ActivityBadgeContainer>
                              )}
                            </>
                          )}
                          
                          <CollapseLink onClick={() => toggleDay(day.day)}>
                            <ChevronUp /> Collapse Day {dayNumber}
                          </CollapseLink>
                        </AccordionInner>
                      </AccordionContentInner>
                    </AccordionContentGrid>
                  </AccordionItem>
                );
              })}
            </AccordionContainer>
          </ScrollReveal>

          {/* Info Grid */}
          <SectionDivider><span>◆</span></SectionDivider>
          <ScrollReveal delay={100}>
            <InfoGrid>
              {itinerary.permits && itinerary.permits.required && (
                <InfoSection>
                  <InfoSectionTitle><ClipboardList /> Permit Information</InfoSectionTitle>
                  <PermitCard>
                    <PermitRow><PermitLabel>Required:</PermitLabel><span>{itinerary.permits.required ? 'Yes' : 'No'}</span></PermitRow>
                    {itinerary.permits.description && (<PermitRow><PermitLabel>Details:</PermitLabel><span>{itinerary.permits.description}</span></PermitRow>)}
                    {itinerary.permits.obtainableIn && (<PermitRow><PermitLabel>Available in:</PermitLabel><span>{itinerary.permits.obtainableIn}</span></PermitRow>)}
                    {itinerary.permits.daysRequired && (<PermitRow><PermitLabel>Processing:</PermitLabel><span>{itinerary.permits.daysRequired}</span></PermitRow>)}
                    {itinerary.permits.assistance && (<PermitRow><PermitLabel>Assistance:</PermitLabel><span>{itinerary.permits.assistance}</span></PermitRow>)}
                  </PermitCard>
                </InfoSection>
              )}
              {itinerary.packing && itinerary.packing.length > 0 && (
                <InfoSection>
                  <InfoSectionTitle><Luggage /> What to Pack</InfoSectionTitle>
                  <PackingGrid>
                    {(showAllPacking ? itinerary.packing : itinerary.packing.slice(0, 5)).map((item: string, i: number) => (<PackingItem key={i}><Circle />{item}</PackingItem>))}
                  </PackingGrid>
                  {itinerary.packing.length > 5 && (
                    <ViewMoreToggle onClick={() => setShowAllPacking(!showAllPacking)}>
                      {showAllPacking ? <>View Less <ChevronUp size={14} /></> : <>View More ({itinerary.packing.length - 5} more) <ChevronDown size={14} /></>}
                    </ViewMoreToggle>
                  )}
                </InfoSection>
              )}
              {itinerary.healthTips && itinerary.healthTips.length > 0 && (
                <InfoSection>
                  <InfoSectionTitle><HeartPulse /> Health Tips</InfoSectionTitle>
                  <div>
                    {(showAllHealthTips ? itinerary.healthTips : itinerary.healthTips.slice(0, 5)).map((tip: string, i: number) => (
                      <HealthTipItem key={i}><Activity size={14} />{tip.replace(/[\u2600-\u27BF\uD83C-\uDBFF\uDC00-\uDFFF\uFE00-\uFE0F]+/g, '').trim()}</HealthTipItem>
                    ))}
                  </div>
                  {itinerary.healthTips.length > 5 && (
                    <ViewMoreToggle onClick={() => setShowAllHealthTips(!showAllHealthTips)}>
                      {showAllHealthTips ? <>View Less <ChevronUp size={14} /></> : <>View More ({itinerary.healthTips.length - 5} more) <ChevronDown size={14} /></>}
                    </ViewMoreToggle>
                  )}
                </InfoSection>
              )}
            </InfoGrid>
          </ScrollReveal>

          {/* Pricing */}
          <SectionDivider><span>◆</span></SectionDivider>
          {(itinerary.pricing || itinerary.carPricingTable || itinerary.tour_type === 'bike') && (
            <ScrollReveal delay={150}>
              <PricingSection>
                <PricingTitle><Tag /> Package Pricing</PricingTitle>

                {/* Bike / Trek → Contact admin message instead of tables */}
                {itinerary.tour_type && itinerary.tour_type !== 'car' ? (
                  <div style={{
                    textAlign: 'center',
                    padding: `${getSpacing('3xl')} ${getSpacing('xl')}`,
                  }}>
                    <BodyText style={{
                      fontSize: '1.05rem',
                      fontStyle: 'italic',
                      color: getColor('primary.800'),
                      marginBottom: getSpacing('lg'),
                      lineHeight: 1.7,
                    }}>
                      For detailed pricing, please contact our team
                    </BodyText>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: getSpacing('sm') }}>
                      <a href="tel:+916005365063" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: getColor('primary.700'), textDecoration: 'none', fontSize: '0.95rem', fontWeight: 500 }}>
                        <Phone size={16} /> +91 6005365063
                      </a>
                      <a href="tel:+919419227555" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: getColor('primary.700'), textDecoration: 'none', fontSize: '0.95rem', fontWeight: 500 }}>
                        <Phone size={16} /> +91 9419227555
                      </a>
                    </div>
                  </div>
                ) : (
                  <>
                {itinerary.pricing?.description && (
                  <BodyText style={{ marginBottom: getSpacing('lg'), color: getColor('primary.800'), fontSize: DesignSystem.typography.scale.bodySmall.size }}>
                    {itinerary.pricing.description}
                  </BodyText>
                )}

                {/* Car Pricing Table — Pivot + Tier Details */}
                {itinerary.carPricingTable && itinerary.carPricingTable.tiers && itinerary.carPricingTable.tiers.length > 0 && (() => {
                  const cpt = itinerary.carPricingTable!;
                  // Build pivot data: group sizes as rows, tiers as columns
                  const allPacks = Array.from(new Set(cpt.tiers.flatMap((t: CarPricingTier) => t.entries.map((e: CarPricingEntry) => e.packs)))).sort((a: number, b: number) => a - b);
                  const tierMap: Record<string, Record<number, number>> = {};
                  cpt.tiers.forEach((t: CarPricingTier) => {
                    tierMap[t.name] = {};
                    t.entries.forEach((e: CarPricingEntry) => { tierMap[t.name][e.packs] = e.pricePerPerson; });
                  });
                  const tierNames = cpt.tiers.map((t: CarPricingTier) => t.name);

                  return (
                    <PricingTableWrapper>
                      {/* Pivot Summary Table */}
                      <PivotTableTitle>Price per Person Comparison</PivotTableTitle>
                      <PivotTableContainer>
                        <PricingTableScrollWrapper>
                          <PivotTable>
                            <PivotTHead>
                              <tr>
                                <th>Group Size</th>
                                {tierNames.map((name: string) => <th key={name}>{name}</th>)}
                              </tr>
                            </PivotTHead>
                            <PivotTBody>
                              {allPacks.map((packs: number) => {
                                const prices = tierNames.map((name: string) => tierMap[name]?.[packs] ?? null);
                                const validPrices = prices.filter((p): p is number => p !== null);
                                const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : null;
                                return (
                                  <tr key={packs}>
                                    <td>
                                      <GroupSizeLabel><Users size={14} />{packs} Person</GroupSizeLabel>
                                    </td>
                                    {prices.map((price, i) => {
                                      const isBest = price !== null && price === minPrice && validPrices.length > 1;
                                      return isBest ? (
                                        <BestValueCell key={tierNames[i]}>
                                          {price !== null ? `₹${price.toLocaleString('en-IN')}` : '—'}
                                        </BestValueCell>
                                      ) : (
                                        <td key={tierNames[i]}>
                                          {price !== null ? `₹${price.toLocaleString('en-IN')}` : '—'}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </PivotTBody>
                          </PivotTable>
                        </PricingTableScrollWrapper>
                      </PivotTableContainer>

                      {/* Detailed Tier Sections */}
                      <TierSectionTitle>Detailed Breakdown by Category</TierSectionTitle>
                      <TierSectionsWrapper>
                        {cpt.tiers.map((tier: CarPricingTier) => {
                          // Build car-model groups for rowSpan merge
                          const entries = tier.entries;
                          const carModelSpans: Record<number, { label: string; span: number }> = {};
                          let groupStart = 0;
                          let currentModel = entries[0]?.carModel || '';
                          for (let idx = 1; idx <= entries.length; idx++) {
                            const model = idx < entries.length ? entries[idx].carModel : '__END__';
                            if (model && model !== currentModel) {
                              carModelSpans[groupStart] = { label: currentModel || '—', span: idx - groupStart };
                              groupStart = idx;
                              currentModel = model;
                            }
                          }
                          return (
                          <TierCard key={tier.name}>
                            <TierLabel>{tier.name}</TierLabel>
                            <PricingTableScrollWrapper>
                              <TierTable>
                                <thead>
                                  <tr>
                                    <th>Car Model</th>
                                    <th>Group Size</th>
                                    <th>Price / Person</th>
                                    <th>Room Split</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {entries.map((entry: CarPricingEntry, i: number) => (
                                    <tr key={i}>
                                      {carModelSpans[i] && (
                                        <td rowSpan={carModelSpans[i].span} style={{ verticalAlign: 'middle' }}>
                                          {carModelSpans[i].label}
                                        </td>
                                      )}
                                      <td>{entry.packs} Person</td>
                                      <td className="price-cell">₹{entry.pricePerPerson.toLocaleString('en-IN')}</td>
                                      <td>{entry.roomSplit || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </TierTable>
                            </PricingTableScrollWrapper>
                          </TierCard>
                          );
                        })}
                      </TierSectionsWrapper>

                      <GSTNotice>* All prices exclude 5% GST</GSTNotice>
                    </PricingTableWrapper>
                  );
                })()}

                {/* Fallback: original car category cards (if no carPricingTable) */}
                {!itinerary.carPricingTable && itinerary.pricing?.carCategories && itinerary.pricing.carCategories.length > 0 && (
                  <CarCategoryGrid>
                    {itinerary.pricing.carCategories.map((cat: any, i: number) => (
                      <CarCategoryCard key={i}>
                        <div className="category-label">Category {cat.category}</div>
                        <div className="vehicles">{cat.vehicles.join(' / ')}</div>
                        {cat.totalPrice && (
                          <>
                            <div className="price">{'\u20B9'}{cat.totalPrice.toLocaleString()}</div>
                            <div className="price-note">Total package price</div>
                          </>
                        )}
                      </CarCategoryCard>
                    ))}
                  </CarCategoryGrid>
                )}

                {(itinerary.pricing?.includes || itinerary.pricing?.excludes) && (
                  <IncludesExcludes>
                    {itinerary.pricing?.includes && itinerary.pricing.includes.length > 0 && (
                      <IncludesBox>
                        <h4><Check /> Included</h4>
                        <ul>{(showAllIncludes ? itinerary.pricing.includes : itinerary.pricing.includes.slice(0, 5)).map((item: string, i: number) => (<li key={i}><Check />{item}</li>))}</ul>
                        {itinerary.pricing.includes.length > 5 && (
                          <ViewMoreToggle onClick={() => setShowAllIncludes(!showAllIncludes)}>
                            {showAllIncludes ? <>View Less <ChevronUp size={14} /></> : <>View More ({itinerary.pricing.includes.length - 5} more) <ChevronDown size={14} /></>}
                          </ViewMoreToggle>
                        )}
                      </IncludesBox>
                    )}
                    {itinerary.pricing?.excludes && itinerary.pricing.excludes.length > 0 && (
                      <ExcludesBox>
                        <h4><X /> Not Included</h4>
                        <ul>{(showAllExcludes ? itinerary.pricing.excludes : itinerary.pricing.excludes.slice(0, 5)).map((item: string, i: number) => (<li key={i}><X />{item}</li>))}</ul>
                        {itinerary.pricing.excludes.length > 5 && (
                          <ViewMoreToggle onClick={() => setShowAllExcludes(!showAllExcludes)}>
                            {showAllExcludes ? <>View Less <ChevronUp size={14} /></> : <>View More ({itinerary.pricing.excludes.length - 5} more) <ChevronDown size={14} /></>}
                          </ViewMoreToggle>
                        )}
                      </ExcludesBox>
                    )}
                  </IncludesExcludes>
                )}
                  </>
                )}
              </PricingSection>
            </ScrollReveal>
          )}

          {/* CTA */}
          <SectionDivider><span>◆</span></SectionDivider>
          <ScrollReveal delay={200}>
            <CTASection>
              <CTAHeading>Your journey, your way</CTAHeading>
              <CTASubtext>Book your complete {itinerary.duration}-day adventure</CTASubtext>
              <CTAButton onClick={openBookingForm}>Book This Journey</CTAButton>
            </CTASection>
          </ScrollReveal>
        </ContentWrapper>
      </MainLayout>

      <BackToTopButton visible={showBackToTop} onClick={scrollToTop}><ArrowUp size={20} /></BackToTopButton>

      <BookingForm
        destination={{ id: 1, title: state.charAt(0).toUpperCase() + state.slice(1), state: state, duration: `${itinerary.duration} days` }}
        isOpen={isBookingFormOpen}
        onClose={() => setIsBookingFormOpen(false)}
        onBack={() => setIsBookingFormOpen(false)}
        onNavigateTo={onNavigateTo}
      />

      <EnquiryDialog
        isOpen={isEnquiryOpen}
        onClose={() => setIsEnquiryOpen(false)}
        sourcePage="detailed-itinerary"
        itineraryId={itineraryId}
        itineraryTitle={itinerary?.name}
      />
    </PageContainer>
  );
};

export default DetailedItineraryPage;
